import { useCallback } from "react";
import { useAuthenticated, useSignOut } from "../auth";
import { sessionHeartbeatInterval } from "../config";
import { usePortalApi } from "../db/portal-api";
import { useNetworkStatus } from "../sync";
import { useInterval } from "../utils";

// Detects a session revoked server-side (e.g. another device logged in with the same,
// non-Mehrfachnutzung license — see /device/login) and signs this device out promptly
// instead of leaving it silently "logged in" with a dead portal key until some
// unrelated sync call happens to fail. Only checked while online, and network errors
// are ignored — offline devices must keep working undisturbed, and only an explicit
// 401 (session no longer exists) triggers sign-out.
export default function SessionHeartbeat() {
  const { portalKey } = useAuthenticated();
  const { useNewApi, baseUrl } = usePortalApi();
  const { effectiveType } = useNetworkStatus();
  const signOut = useSignOut();

  const checkSession = useCallback(async () => {
    if (!useNewApi || !baseUrl || !portalKey) return;
    if (effectiveType === "none") return;

    try {
      const response = await fetch(`${baseUrl}/api/device/session`, {
        headers: { authorization: `Bearer ${portalKey}` },
      });
      if (response.status === 401) {
        await signOut(
          new Error(
            "Diese Lizenz wurde auf einem anderen Gerät angemeldet. Sie wurden daher hier abgemeldet.",
          ),
        );
      }
    } catch {
      // Offline or unreachable — not a session revocation, ignore.
    }
  }, [useNewApi, baseUrl, portalKey, effectiveType, signOut]);

  useInterval(checkSession, sessionHeartbeatInterval);

  return null;
}
